/**
 * Minimal RIFF/WAVE writer, 16-bit PCM.
 *
 * Used for exactly one job: turning audio the chart engine cannot decode on this
 * machine into audio it can. It is not a general encoder and should not become
 * one — the engine reads WAV through the standard library and everything else
 * through libsndfile, so the only question this module answers is "can we hand
 * over a file at all".
 *
 * Sample rate and channel count are preserved. Downmixing is the engine's job
 * (`load_audio` averages the channels itself), and doing it here as well would
 * put the same rule in two languages, where it could drift.
 *
 * Pure: no DOM, no Web Audio. It takes channel arrays, so it can be tested
 * without an `AudioContext` and reused by any caller that has samples.
 */

const HEADER_BYTES = 44;
const BITS_PER_SAMPLE = 16;
/** 1 = PCM, the only format Python's `wave` module reads. */
const FORMAT_PCM = 1;

export interface WavSource {
  /** One Float32Array per channel, all the same length, nominally -1..1. */
  readonly channels: readonly Float32Array[];
  readonly sampleRate: number;
}

/** Encodes to a standalone `ArrayBuffer` holding a complete RIFF/WAVE file. */
export function encodeWav(source: WavSource): ArrayBuffer {
  const channelCount = source.channels.length;
  if (channelCount === 0) throw new RangeError('encodeWav needs at least one channel.');
  if (!Number.isFinite(source.sampleRate) || source.sampleRate <= 0) {
    throw new RangeError(`encodeWav needs a positive sample rate, got ${source.sampleRate}.`);
  }
  const frames = source.channels[0]?.length ?? 0;
  for (const channel of source.channels) {
    if (channel.length !== frames) {
      throw new RangeError('encodeWav needs every channel to be the same length.');
    }
  }

  const blockAlign = channelCount * (BITS_PER_SAMPLE / 8);
  const dataBytes = frames * blockAlign;
  const buffer = new ArrayBuffer(HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  // Everything after this field, which is the file length minus the 8 bytes of
  // "RIFF" plus this size itself.
  view.setUint32(4, HEADER_BYTES - 8 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, FORMAT_PCM, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, source.sampleRate, true);
  view.setUint32(28, source.sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, BITS_PER_SAMPLE, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);

  // Interleaved, frame by frame, which is what WAV means by a frame.
  const samples = new Int16Array(buffer, HEADER_BYTES, frames * channelCount);
  for (let channel = 0; channel < channelCount; channel += 1) {
    const values = source.channels[channel] as Float32Array;
    for (let frame = 0; frame < frames; frame += 1) {
      samples[frame * channelCount + channel] = toPcm16(values[frame] as number);
    }
  }
  return buffer;
}

/**
 * Float to signed 16-bit.
 *
 * Clamped before scaling, and scaled by 32767 rather than 32768, so a sample
 * sitting exactly at 1.0 lands on the top of the range instead of wrapping to
 * the bottom of it. This matches what the engine does on the way out
 * (`audio_io.encode_wav`), which keeps a round trip through the bridge from
 * changing the waveform in a way neither side intended.
 *
 * An infinity is out of range and clamps like any other out-of-range value; a
 * NaN is not a magnitude at all and becomes silence. Neither should reach here
 * from a decoded buffer, but "loudest possible sample" is a worse guess for a
 * NaN than "nothing", and "nothing" is a worse guess for an infinity than "as
 * loud as this format goes".
 */
function toPcm16(value: number): number {
  if (Number.isNaN(value)) return 0;
  const clamped = value < -1 ? -1 : value > 1 ? 1 : value;
  return Math.round(clamped * 32767);
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let index = 0; index < text.length; index += 1) {
    view.setUint8(offset + index, text.charCodeAt(index));
  }
}
